import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {AxiosResponse} from 'axios';
import {Field, Form, Formik} from 'formik';
import {IAxiosGetResponse, IInstitutionDto, IServiceDto} from '../../interfaces/IUtilities';
import * as Yup from "yup";
import {create, getAll, getById, update} from '../_request';
import Loader from '../../modules/layout/components/Loader';
import {useToast} from '../../modules/layout/components/ToastContext';
import BackButton from '../../modules/layout/components/BackButton';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import {useTranslation} from 'react-i18next';
import {ServiceSecurityImplicitTypeText} from "../../helpers/EnumeratorHelper";
import FormikSelect from '../../modules/layout/components/Select/FormikSelect';

type InstitutionOption = {
    value: string;
    label: string;
};

const ServiceForm: React.FC = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const {showToast} = useToast();
    const [loading, setLoading] = useState(true);
    const {t} = useTranslation();
    const [institutions, setInstitutions] = useState([] as IInstitutionDto[]);

    const institutionOptions = useMemo<InstitutionOption[]>(() => (
        (institutions ?? [])
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((institution) => ({
                value: institution.id,
                label: institution.name
            }))
    ), [institutions]);

    const [initialValues, setInitialValues] = useState<IServiceDto>({
        id: '00000000-0000-0000-0000-000000000000',
        name: '',
        code: '',
        ownerId: '00000000-0000-0000-0000-000000000000',
        isDeleted: false,
        institutionId: "",
        isApproved: false
    });

    const fetchInstitutions = async () => {
        try {    
        
            let result = await getAll('/api/institutions/gettotal', '');
            let dataResults = result?.data as IInstitutionDto[];
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setInstitutions(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', {code: String(respStatus)}), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus)} ));
            }
        } catch (error) {
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            navigate('/');
        }
    }
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                let result = await getById("/api/services/", id!);
                let respStatus = result?.status || result?.response.status;

                if (respStatus === 200) {
                    setInitialValues(result.data);
                    setLoading(false);
                } else if (respStatus === 403) {
                    navigate("/error/403");
                } else if (respStatus === 401) {
                    navigate("/auth/login");
                } else if (respStatus === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(respStatus)}));
                }
            } catch (error) {
                setLoading(false);
                console.log(t('ConnectionError', {error: String(error)}));
                showToast(t('ConnectionError', {error: String(error)}), 'error');
                navigate("/services/list");
            }
        }

        if (isEdit && id) {
            fetchData();
        } else {
            setLoading(false);
        }

        fetchInstitutions();

    }, [id, isEdit]);

    const handleSubmit = async (values: IServiceDto, { setErrors }: any) => {
        setLoading(true);
        if (isEdit) {
            try {
                let result = await update('/api/services/', {...values}) as AxiosResponse;
                if (result.status === 200) {
                    setLoading(false);
                    showToast(t('ServiceUpdatedSuccessfully'), 'success');
                    navigate("/services/list");
                } else if (result.status === 400 && result.data?.errors) {
                    setLoading(false);

                    const formikErrors: Record<string, string> = {};
                    for (const key in result.data.errors) {
                        formikErrors[key] = t(result.data.errors[key]);
                    }

                    setErrors(formikErrors);
                } else if (result.status === 403) {
                    navigate('/error/403');
                } else if (result.status === 401) {
                    navigate('/auth/login');
                } else if (result.status === 500) {
                    navigate('/error/500');
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }
        } else {
            try {
                let result = await create('/api/services', { ...values }) as AxiosResponse;
                if (result.status === 200) {
                    setLoading(false);
                    showToast(t('ServiceCreatedSuccessfully'), 'success');
                    navigate("/services/list");
                } else if (result.status === 400 && result.data?.errors) {
                    setLoading(false);

                    const formikErrors: Record<string, string> = {};
                    for (const key in result.data.errors) {
                        formikErrors[key] = t(result.data.errors[key]);
                    }

                    setErrors(formikErrors);
                } else if (result.status === 403) {
                    navigate('/error/403');
                } else if (result.status === 401) {
                    navigate('/auth/login');
                } else if (result.status === 500) {
                    navigate('/error/500');
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }
        }
    };

    const validationSchema = Yup.object({
        name: Yup.string().required(t('Required')),
        code: Yup.string().required(t('Required')),
        institutionId: Yup.string().required(t('PleaseSubmitInstitution')),
    });


    return (
        <div className="container mt-4 gg-container-form">

            <AppBreadcrumb
                items={[
                    {title: t("Home"), href: "/"},
                    {title: t("Services"), href: "/services/list"},
                    {title: isEdit ? "Edit Service" : "Create Service"} // Active page (no href)
                ]}/>

            <h3>{isEdit ? t('EditService') : t('Create Service')}</h3>

            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}
                    enableReinitialize>
                {({errors, touched, isSubmitting, values, setFieldValue}) => (
                    <Form>
                        {isSubmitting && (
                            <Loader />
                        )}

                        <div className="row g-3 mb-3">
                            <div className="col-md-12">
                                <div className="row">
                                    <div className="col-md-12">
                                        {/*name*/}
                                <div className="mb-3">
                                    <label className="form-label">{t('Name')}</label>
                                    <Field className="form-control" name="name"/>
                                    {touched.name && errors.name &&
                                        <div className="text-danger">{errors.name}</div>}
                                </div>
                                    </div>
                                    <div className="col-md-6">
                                        {/*code*/}
                                <div className="mb-3">
                                    <label>{t('Code')}</label>
                                    <Field name="code" className="form-control"/>
                                    {touched.code && errors.code &&
                                        <div className="text-danger">{errors.code}</div>}
                                </div>
                                    </div>
                                    <div className="col-md-6">

                                        {/*Institution*/}
                                        <div className="mb-3">
                                            <label className="w-100">
                                                {t('Institution')}
                                                <FormikSelect
                                                    name="institutionId"
                                                    options={institutionOptions}
                                                    isClearable
                                                    placeholder={`-- ${t('SelectOption')} --`}
                                                />
                                            </label>
                                            {touched.institutionId && errors.institutionId &&
                                                <div className="text-danger">{errors.institutionId}</div>}
                                        </div>

                                    </div>
                                </div>
                                
                                

                            </div>
                        </div>

                        <div className="mb-3">
                            <BackButton goTo={"/services/list"}/>

                            <button type="submit" className="btn ms-3 gg-btn-submit">
                                {isEdit ? t('Update') : t('Create')}
                            </button>
                        </div>

                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default ServiceForm;
